import type {
  LiveInputConfiguration,
  LiveInputConfigurationInput,
  LiveInputsConfigurationResponse,
  ScheduleConfiguration,
  ScheduleConfigurationInput,
  SchedulesConfigurationResponse,
  EventGenerationResult,
  PcoServicesSyncResult,
  PcoServicesSyncSettingsResponse,
  GeneralSettingsResponse,
  PublicSiteSettings,
  RetentionSettingsResponse,
  RolePermissionsResponse,
  ServiceTypeConfiguration,
  ServiceTypeConfigurationInput,
  ServiceTypesConfigurationResponse,
} from "@friends-church-dvr/contracts";
import { formShell } from "./configuration-disclosure";
import { formatScheduleTime } from "./time-format";

interface RenderOptions {
  app: HTMLDivElement;
  brand: string;
  menu: string;
}

const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>'"]/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        "'": "&#39;",
        '"': "&quot;",
      })[character] ?? character,
  );

const value = (input: string | null): string => escapeHtml(input ?? "");

const errorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = (await response.json()) as { error?: { message?: string } };
    return payload.error?.message ?? "The request could not be completed.";
  } catch {
    return "The request could not be completed.";
  }
};

const field = (
  label: string,
  name: string,
  currentValue: string,
  options: { type?: string; required?: boolean; hint?: string } = {},
): string => `
  <label class="config-field">
    <span>${escapeHtml(label)}</span>
    <input
      type="${options.type ?? "text"}"
      name="${escapeHtml(name)}"
      value="${value(currentValue)}"
      ${options.required ? "required" : ""}
    >
    ${options.hint ? `<small>${escapeHtml(options.hint)}</small>` : ""}
  </label>
`;

const activeField = (checked: boolean): string => `
  <label class="config-toggle">
    <input type="checkbox" name="isActive" ${checked ? "checked" : ""}>
    <span><strong>Active</strong><small>Available to schedules and application workflows.</small></span>
  </label>
`;

const configurableRoles = ["viewer", "editor", "manager"] as const;
const roleLabel = (role: string): string => role.charAt(0).toUpperCase() + role.slice(1);

const configurationSection = (input: {
  id: string;
  eyebrow: string;
  title: string;
  status: string;
  body: string;
}): string => `
  <details class="configuration-section" id="${input.id}">
    <summary class="configuration-section-summary">
      <span><small>${escapeHtml(input.eyebrow)}</small><strong>${escapeHtml(input.title)}</strong></span>
      <span><small>${escapeHtml(input.status)}</small><i aria-hidden="true">+</i></span>
    </summary>
    <div class="configuration-section-body">${input.body}</div>
  </details>
`;

const generalSettingsForm = (settings: GeneralSettingsResponse): string => `
  <form class="general-settings-form" id="general-settings-form">
    <div class="config-grid two-column">
      ${field("Organization Name", "organizationName", settings.organizationName, { required: true })}
      ${field("Site Name", "siteName", settings.siteName, { required: true })}
    </div>
    <fieldset class="theme-template-fieldset">
      <legend>Appearance template</legend>
      <p>Select a managed brand template or keep full control of the manual appearance settings.</p>
      <div class="theme-template-options">
        <label class="theme-template-option">
          <input type="radio" name="themeTemplate" value="friends-church-online" ${settings.themeTemplate === "friends-church-online" ? "checked" : ""}>
          <span class="theme-template-preview friends-church-online-preview" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i></span>
          <span><strong>Friends Church</strong><small>Editorial serif copy, expanded display type, square geometry, and the complete Friends tonal palette.</small></span>
        </label>
        <label class="theme-template-option">
          <input type="radio" name="themeTemplate" value="custom" ${settings.themeTemplate === "custom" ? "checked" : ""}>
          <span class="theme-template-preview custom-theme-preview" aria-hidden="true"><i style="background:${settings.backgroundColor}"></i><i style="background:${settings.fontColor}"></i><i style="background:${settings.featureColor}"></i></span>
          <span><strong>Custom</strong><small>Use the manual colors and font below.</small></span>
        </label>
        <label class="theme-template-option">
          <input type="radio" name="themeTemplate" value="default" ${settings.themeTemplate === "default" ? "checked" : ""}>
          <span class="theme-template-preview default-theme-preview" aria-hidden="true"><i></i><i></i><i></i></span>
          <span><strong>Default</strong><small>The original locked DVR palette with DM Sans typography.</small></span>
        </label>
      </div>
    </fieldset>
    <fieldset class="manual-theme-settings" data-manual-theme-settings ${settings.themeTemplate === "custom" ? "" : "disabled"}>
      <legend>Manual appearance</legend>
      <p data-template-managed-note>${settings.themeTemplate === "custom" ? "These values apply when the Custom template is selected." : `Managed by the ${settings.themeTemplate === "default" ? "Default" : "Friends Church"} template. Select Custom to edit these values.`}</p>
      <div class="config-grid two-column">
        ${["backgroundColor", "fontColor", "featureColor"].map((name) => {
          const labels = { backgroundColor: "Background color", fontColor: "Font color", featureColor: "Feature color" };
          const color = settings[name as keyof PublicSiteSettings] as string;
          return `<label class="config-field color-field"><span>${labels[name as keyof typeof labels]}</span><span class="color-input-row"><input type="color" data-color-picker="${name}" value="${color}"><input type="text" name="${name}" value="${color}" pattern="#[0-9A-Fa-f]{6}" required maxlength="7"></span></label>`;
        }).join("")}
        <label class="config-field">
          <span>Font type</span>
          <select name="fontFamily">
            ${["DM Sans", "Arial", "Georgia", "Newsreader", "PT Serif", "System"].map((font) => `<option value="${font}" ${settings.fontFamily === font ? "selected" : ""}>${font}</option>`).join("")}
          </select>
        </label>
      </div>
    </fieldset>
    <label class="config-toggle logo-display-toggle">
      <input type="checkbox" name="showLogo" ${settings.logoDataUrl ? "checked" : ""}>
      <span><strong>Display main logo</strong><small>Turn this off to remove the logo from every site header.</small></span>
    </label>
    <input type="hidden" name="videoCommentsEnabled" value="${settings.videoCommentsEnabled ? "true" : "false"}">
    <input type="hidden" name="viewingAnalyticsEnabled" value="${settings.viewingAnalyticsEnabled ? "true" : "false"}">
    <div class="logo-upload-field">
      <div class="logo-preview" data-logo-preview>${settings.logoDataUrl ? `<img src="${value(settings.logoDataUrl)}" alt="Current main logo">` : '<span>No logo configured</span>'}</div>
      <label class="config-field"><span>Main logo</span><input type="file" name="logoFile" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"><small>PNG, JPEG, WebP, GIF, or SVG. Maximum 256 KB.</small></label>
      <input type="hidden" name="logoDataUrl" value="${value(settings.logoDataUrl)}">
    </div>
    <p class="form-message" role="status" aria-live="polite"></p>
    <button class="primary-button compact" type="submit">Save General settings</button>
  </form>
`;

const videoPlaybackSettingsForm = (settings: GeneralSettingsResponse): string => `
  <form class="video-playback-settings-form" id="video-playback-settings-form">
    <label class="config-toggle">
      <input type="checkbox" name="videoCommentsEnabled" ${settings.videoCommentsEnabled ? "checked" : ""}>
      <span><strong>Enable video notes and comments</strong><small>Show the notes and comments section on Video player pages. Editors and above can add comments.</small></span>
    </label>
    <label class="config-toggle">
      <input type="checkbox" name="viewingAnalyticsEnabled" ${settings.viewingAnalyticsEnabled ? "checked" : ""}>
      <span><strong>Enable viewing analytics</strong><small>Show live and recorded viewing analytics to administrators on Video player pages.</small></span>
    </label>
    <p class="form-message" role="status" aria-live="polite"></p>
    <button class="primary-button compact" type="submit">Save Video Playback</button>
  </form>
`;

const retentionSettingsForm = (settings: RetentionSettingsResponse): string => `
  <form class="retention-settings-form" id="retention-settings-form">
    <div class="config-grid two-column">
      ${field("Online retention days", "onlineDays", String(settings.onlineDays), { type: "number", required: true, hint: "Default time an original archived Event remains on Cloudflare Stream." })}
      ${field("Restored online retention days", "restoredOnlineDays", String(settings.restoredOnlineDays), { type: "number", required: true, hint: "Fresh retention window after an archived Event is restored." })}
    </div>
    <p class="config-warning"><strong>Google Drive archive retention:</strong> Never delete. Automatic archive deletion is not implemented in this milestone.</p>
    <p class="form-message" role="status" aria-live="polite"></p>
    <button class="primary-button compact" type="submit">Save Retention</button>
  </form>
`;

const pcoSyncSettingsForm = (settings: PcoServicesSyncSettingsResponse): string => {
  const option = (
    frequency: PcoServicesSyncSettingsResponse["frequency"],
    title: string,
    description: string,
  ): string => `
    <label class="pco-sync-option">
      <input type="radio" name="frequency" value="${frequency}" ${settings.frequency === frequency ? "checked" : ""}>
      <span><strong>${title}</strong><small>${description}</small></span>
    </label>`;
  const lastSync = settings.lastSuccessfulSyncAt
    ? new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
        hour12: true,
        timeZone: settings.timezone,
      }).format(new Date(settings.lastSuccessfulSyncAt))
    : "No successful synchronization recorded yet";
  return `
    <form class="pco-sync-settings-form" id="pco-sync-settings-form">
      <fieldset>
        <legend>Automatic syncing</legend>
        <div class="pco-sync-options">
          ${option("manual", "Manual only", "Automatic synchronization is disabled.")}
          ${option("weekly", "Weekly", "Every Thursday at 3:00 PM Eastern Time.")}
          ${option("daily", "Daily", "Every day at 3:00 PM Eastern Time.")}
          ${option("every_four_hours", "Every 4 hours", "Four hours after the last successful synchronization.")}
        </div>
      </fieldset>
      <p class="pco-sync-last-run">Last successful sync: ${escapeHtml(lastSync)}</p>
      <div class="pco-sync-actions">
        <button class="primary-button compact" type="submit">Save Auto Sync</button>
        <span class="form-message" id="pco-sync-settings-message" role="status" aria-live="polite"></span>
      </div>
    </form>`;
};

const rolePermissionMatrix = (payload: RolePermissionsResponse): string => {
  const enabled = new Set(payload.grants.filter((grant) => grant.enabled).map((grant) => `${grant.role}:${grant.permissionKey}`));
  const roles = [...configurableRoles, "administrator"] as const;
  return `
    <p class="section-description">Choose the capabilities granted by each role. Service Type scope remains separate. Configuration permissions and Administrator access are locked.</p>
    <div class="permission-matrix-scroll" tabindex="0" aria-label="Role permission matrix">
      <table class="permission-matrix"><thead><tr><th scope="col">Permission</th>${roles.map((role) => `<th scope="col">${roleLabel(role)}</th>`).join("")}</tr></thead>
      <tbody>${payload.permissions.map((permission) => `<tr><th scope="row"><strong>${escapeHtml(permission.label)}</strong><small>${escapeHtml(permission.description)}</small>${permission.implementationStatus === "planned" ? '<span class="planned-badge">Planned</span>' : ""}</th>${roles.map((role) => {
        const locked = role === "administrator" || !permission.configurable;
        return `<td><label class="permission-toggle"><input type="checkbox" data-role="${role}" data-permission-key="${permission.key}" ${enabled.has(`${role}:${permission.key}`) ? "checked" : ""} ${locked ? "disabled" : ""}><span class="sr-only">${roleLabel(role)}: ${escapeHtml(permission.label)}${locked ? ", locked" : ""}</span></label></td>`;
      }).join("")}</tr>`).join("")}</tbody></table>
    </div>
    <div class="permission-actions"><button class="primary-button compact" type="button" id="save-role-permissions">Save User Roles</button><span class="form-message" id="permission-message" role="status" aria-live="polite"></span></div>
  `;
};

const serviceTypeForm = (serviceType?: ServiceTypeConfiguration): string =>
  formShell({
    resource: "service-type",
    id: serviceType?.id,
    title: serviceType?.name ?? "Add Service Type",
    subtitle: serviceType
      ? `${serviceType.slug} · ${serviceType.isActive ? "Active" : "Inactive"}`
      : "Create a new DVR content category",
    create: !serviceType,
    deleteLabel: serviceType ? "Delete Service Type" : undefined,
    body: `
      <div class="config-grid two-column">
        ${field("Name", "name", serviceType?.name ?? "", { required: true })}
        ${field("Slug", "slug", serviceType?.slug ?? "", {
          required: true,
          hint: "Lowercase letters, numbers, and hyphens.",
        })}
        ${field(
          "Planning Center Service Type ID",
          "pcoServiceTypeId",
          serviceType?.pcoServiceTypeId ?? "",
          { hint: "Numeric mapping ID; not an OAuth credential." },
        )}
        ${field("Sort order", "sortOrder", String(serviceType?.sortOrder ?? 100), {
          type: "number",
          required: true,
        })}
      </div>
      <label class="config-field">
        <span>Description</span>
        <textarea name="description" rows="3">${value(serviceType?.description ?? "")}</textarea>
      </label>
      ${activeField(serviceType?.isActive ?? true)}
    `,
  });

const liveInputForm = (liveInput?: LiveInputConfiguration): string =>
  formShell({
    resource: "live-input",
    id: liveInput?.id,
    title: liveInput?.name ?? "Add Live Input",
    subtitle: liveInput
      ? `${liveInput.sourceLabel ?? "Cloudflare Stream"} · ${liveInput.isActive ? "Active" : "Inactive"}`
      : "Register a Cloudflare Stream Live Input",
    create: !liveInput,
    deleteLabel: liveInput ? "Delete Live Input" : undefined,
    body: `
      <div class="config-grid two-column">
        ${field("Name", "name", liveInput?.name ?? "", { required: true })}
        ${field(
          "Cloudflare Live Input UID",
          "cloudflareLiveInputUid",
          liveInput?.cloudflareLiveInputUid ?? "",
          {
            required: true,
            hint: "Store only the Live Input UID. Never enter an RTMPS stream key.",
          },
        )}
        ${field("Source label", "sourceLabel", liveInput?.sourceLabel ?? "", {
          hint: "For example: Resi sanctuary encoder.",
        })}
        <label class="config-field">
          <span>Recording segmentation</span>
          <select name="recordingSegmentationMode">
            <option value="continuous" ${(liveInput?.recordingSegmentationMode ?? "continuous") === "continuous" ? "selected" : ""}>Continuous Source Recording</option>
            <option value="cycle_between_events" ${liveInput?.recordingSegmentationMode === "cycle_between_events" ? "selected" : ""}>Cycle between adjacent Events</option>
          </select>
          <small>When enabled, DVR briefly disables and re-enables this Cloudflare Live Input during safe 10–30 minute gaps between Events.</small>
        </label>
      </div>
      <label class="config-field">
        <span>Notes</span>
        <textarea name="notes" rows="3">${value(liveInput?.notes ?? "")}</textarea>
      </label>
      ${activeField(liveInput?.isActive ?? true)}
    `,
  });

const weekdayOptions = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const selectOptions = (
  entries: Array<{ value: string; label: string; active?: boolean }>,
  selected: string | null,
  includeEmpty = false,
): string => `${includeEmpty ? '<option value="">Not assigned</option>' : ""}${entries
  .map(
    (entry) =>
      `<option value="${escapeHtml(entry.value)}" ${entry.value === selected ? "selected" : ""}>${escapeHtml(entry.label)}${entry.active === false ? " (inactive)" : ""}</option>`,
  )
  .join("")}`;

const scheduleForm = (
  serviceTypes: ServiceTypeConfiguration[],
  liveInputs: LiveInputConfiguration[],
  schedule?: ScheduleConfiguration,
): string =>
  formShell({
    resource: "schedule",
    id: schedule?.id,
    title: schedule?.name ?? "Add Schedule",
    subtitle: schedule
      ? `${weekdayOptions[schedule.weekday]} ${formatScheduleTime(schedule.startTimeLocal)}–${formatScheduleTime(schedule.endTimeLocal)} · ${schedule.isActive ? "Active" : "Inactive"}`
      : "Create a recurring Eastern Time capture window",
    create: !schedule,
    deleteLabel: schedule ? "Delete Schedule" : undefined,
    body: `
      <div class="config-grid two-column">
        ${field("Name", "name", schedule?.name ?? "", { required: true })}
        <label class="config-field">
          <span>Service Type</span>
          <select name="serviceTypeId" required>
            ${selectOptions(
              serviceTypes.map((item) => ({
                value: item.id,
                label: item.name,
                active: item.isActive,
              })),
              schedule?.serviceTypeId ?? serviceTypes[0]?.id ?? null,
            )}
          </select>
        </label>
        <label class="config-field">
          <span>Live Input</span>
          <select name="liveInputId">
            ${selectOptions(
              liveInputs.map((item) => ({
                value: item.id,
                label: item.name,
                active: item.isActive,
              })),
              schedule?.liveInputId ?? null,
              true,
            )}
          </select>
          <small>Required before this Schedule can be activated.</small>
        </label>
        <label class="config-field">
          <span>Weekday</span>
          <select name="weekday">
            ${weekdayOptions
              .map(
                (day, index) =>
                  `<option value="${index}" ${(schedule?.weekday ?? 0) === index ? "selected" : ""}>${day}</option>`,
              )
              .join("")}
          </select>
        </label>
        ${field("Start time", "startTimeLocal", schedule?.startTimeLocal ?? "09:00", {
          type: "time",
          required: true,
        })}
        ${field("End time", "endTimeLocal", schedule?.endTimeLocal ?? "10:00", {
          type: "time",
          required: true,
        })}
        ${field("Pre-roll minutes", "preRollMinutes", String(schedule?.preRollMinutes ?? 0), {
          type: "number",
          required: true,
        })}
        ${field("Post-roll minutes", "postRollMinutes", String(schedule?.postRollMinutes ?? 0), {
          type: "number",
          required: true,
        })}
        ${field("Effective start date", "effectiveStartDate", schedule?.effectiveStartDate ?? "", {
          type: "date",
        })}
        <div class="config-field schedule-end-date-field">
          <span>Effective end date</span>
          <div class="config-date-clear-row">
            <input type="date" name="effectiveEndDate" aria-label="Effective end date" value="${value(schedule?.effectiveEndDate ?? "")}">
            <button class="secondary-button compact clear-schedule-end-date" type="button">Clear end date</button>
          </div>
          <small>Leave blank for a Schedule with no effective end date.</small>
        </div>
      </div>
      ${schedule ? `
        <section class="schedule-thumbnail-settings">
          <div>
            <h4>Schedule thumbnail</h4>
            <p>Use one custom image as the default for every Event generated by this Schedule.</p>
          </div>
          <div class="event-thumbnail schedule-thumbnail-preview${schedule.hasCustomThumbnail ? " has-artwork" : ""}" aria-hidden="true">
            <span>${escapeHtml(schedule.name.split(/\s+/u).map((part) => part[0] ?? "").join("").slice(0, 3).toUpperCase())}</span>
            ${schedule.hasCustomThumbnail ? `<img src="/api/admin/schedules/${encodeURIComponent(schedule.id)}/thumbnail?v=${encodeURIComponent(schedule.updatedAt)}" alt="">` : ""}
          </div>
          <label class="config-field">
            <span>Custom thumbnail</span>
            <input name="scheduleThumbnail" type="file" accept="image/jpeg,image/png,image/webp">
            <small>JPEG, PNG, or WebP. Maximum 1.5 MB. Landscape images work best.</small>
          </label>
          <div class="thumbnail-actions">
            <button class="secondary-button compact upload-schedule-thumbnail" type="button">${schedule.hasCustomThumbnail ? "Replace Custom Thumbnail" : "Upload Custom Thumbnail"}</button>
            ${schedule.hasCustomThumbnail ? '<button class="secondary-button compact remove-schedule-thumbnail" type="button">Remove Custom Thumbnail</button>' : ""}
          </div>
          <small class="schedule-thumbnail-help">An Event-specific custom image or abbreviation selection overrides this Schedule default. Removing this image returns Events to Planning Center artwork or the default abbreviation.</small>
        </section>
      ` : ""}
      <input type="hidden" name="timezone" value="America/New_York">
      ${activeField(schedule?.isActive ?? false)}
      <p class="config-warning">Schedules use America/New_York. Activation requires both an active Service Type and an active Live Input.</p>
    `,
  });

const textValue = (form: HTMLFormElement, name: string): string =>
  (form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement).value;

const nullableValue = (form: HTMLFormElement, name: string): string | null =>
  textValue(form, name).trim() || null;

const activeValue = (form: HTMLFormElement): boolean =>
  (form.elements.namedItem("isActive") as HTMLInputElement).checked;

const serviceTypeInput = (form: HTMLFormElement): ServiceTypeConfigurationInput => ({
  name: textValue(form, "name"),
  slug: textValue(form, "slug"),
  description: nullableValue(form, "description"),
  pcoServiceTypeId: nullableValue(form, "pcoServiceTypeId"),
  sortOrder: Number(textValue(form, "sortOrder")),
  isActive: activeValue(form),
});

const liveInputInput = (form: HTMLFormElement): LiveInputConfigurationInput => ({
  name: textValue(form, "name"),
  cloudflareLiveInputUid: textValue(form, "cloudflareLiveInputUid"),
  sourceLabel: nullableValue(form, "sourceLabel"),
  notes: nullableValue(form, "notes"),
  recordingSegmentationMode: textValue(
    form,
    "recordingSegmentationMode",
  ) as LiveInputConfigurationInput["recordingSegmentationMode"],
  isActive: activeValue(form),
});

const scheduleInput = (form: HTMLFormElement): ScheduleConfigurationInput => ({
  name: textValue(form, "name"),
  serviceTypeId: textValue(form, "serviceTypeId"),
  liveInputId: nullableValue(form, "liveInputId"),
  weekday: Number(textValue(form, "weekday")),
  startTimeLocal: textValue(form, "startTimeLocal"),
  endTimeLocal: textValue(form, "endTimeLocal"),
  timezone: "America/New_York",
  preRollMinutes: Number(textValue(form, "preRollMinutes")),
  postRollMinutes: Number(textValue(form, "postRollMinutes")),
  effectiveStartDate: nullableValue(form, "effectiveStartDate"),
  effectiveEndDate: nullableValue(form, "effectiveEndDate"),
  isActive: activeValue(form),
});

export const renderAdminConfiguration = async ({
  app,
  brand,
  menu,
}: RenderOptions): Promise<void> => {
  app.innerHTML = `
    <main class="shell admin-shell configuration-shell">
      <header class="topbar page-title-topbar">
        ${brand}
        <div class="mobile-page-heading" aria-hidden="true"><h1>Platform configuration.</h1></div>
        ${menu}
      </header>
      <section class="admin-intro configuration-intro page-title-intro">
        <h1>Platform configuration.</h1>
      </section>
      <nav class="configuration-nav" aria-label="Configuration sections">
        <a href="#general">General</a>
        <a href="#video-playback">Video Playback</a>
        <a href="#user-roles">User Roles</a>
        <a href="#service-types">Service Types</a>
        <a href="#live-inputs">Live Inputs</a>
        <a href="#schedules">Schedules</a>
        <a href="#retention">Retention</a>
        <a href="#integrations">Integrations</a>
      </nav>
      <div class="admin-notice" id="configuration-notice" role="status" aria-live="polite"></div>
      <div id="configuration-content">
        <article class="config-card skeleton"></article>
      </div>
    </main>
  `;

  const content = document.querySelector<HTMLElement>("#configuration-content");
  const notice = document.querySelector<HTMLElement>("#configuration-notice");
  const openConfigurationSection = (
    hash: string,
    behavior: ScrollBehavior = "smooth",
  ): void => {
    const sectionId = decodeURIComponent(hash.replace(/^#/, ""));
    const section = document.getElementById(sectionId);
    if (!(section instanceof HTMLDetailsElement) || !section.classList.contains("configuration-section")) return;
    section.open = true;
    section.scrollIntoView({ behavior, block: "start" });
  };

  document.querySelectorAll<HTMLAnchorElement>(".configuration-nav a").forEach((link) => {
    link.addEventListener("click", (event) => {
      const target = link.getAttribute("href");
      if (!target?.startsWith("#")) return;
      event.preventDefault();
      history.replaceState(null, "", target);
      openConfigurationSection(target);
    });
  });

  const wireIntegrations = (): void => {
    document.querySelector<HTMLButtonElement>("#sync-pco-services")?.addEventListener("click", (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      const integrationNotice = document.querySelector<HTMLElement>("#integration-notice");
      void (async () => {
        try {
          button.disabled = true;
          button.textContent = "Synchronizing…";
          const response = await fetch("/api/admin/pco-services/sync", {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json" },
            body: "{}",
          });
          if (!response.ok) throw new Error(await errorMessage(response));
          const result = await response.json() as PcoServicesSyncResult;
          if (integrationNotice) {
            integrationNotice.textContent = `Planning Center synchronized: ${result.plansProcessed} Plans, ${result.itemsProcessed} items, ${result.peopleProcessed} people, ${result.assignmentsProcessed} Song/Item Leader assignments, ${result.markersProposed} proposed Markers, and ${result.eventsMatched} Event matches.`;
            integrationNotice.className = "form-message visible";
          }
        } catch (error) {
          if (integrationNotice) {
            integrationNotice.textContent = error instanceof Error ? error.message : "Planning Center Services could not be synchronized.";
            integrationNotice.className = "form-message visible error-message";
          }
        } finally {
          button.disabled = false;
          button.textContent = "Sync PCO Service Manually";
        }
      })();
    });
    document.querySelector<HTMLFormElement>("#pco-sync-settings-form")?.addEventListener("submit", (event) => {
      event.preventDefault();
      const form = event.currentTarget as HTMLFormElement;
      const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
      const message = form.querySelector<HTMLElement>("#pco-sync-settings-message");
      void (async () => {
        try {
          if (button) { button.disabled = true; button.textContent = "Saving…"; }
          if (message) { message.textContent = "Saving…"; message.className = "form-message"; }
          const frequency = String(new FormData(form).get("frequency"));
          const response = await fetch("/api/admin/pco-services/settings", {
            method: "PATCH",
            headers: { Accept: "application/json", "Content-Type": "application/json" },
            body: JSON.stringify({ frequency }),
          });
          if (!response.ok) throw new Error(await errorMessage(response));
          if (notice) {
            notice.textContent = "Planning Center automatic sync settings saved and audited.";
            notice.className = "admin-notice visible";
          }
          await load();
        } catch (error) {
          if (button) { button.disabled = false; button.textContent = "Save Auto Sync"; }
          if (message) {
            message.textContent = error instanceof Error ? error.message : "Automatic sync settings could not be saved.";
            message.className = "form-message error-message";
          }
        }
      })();
    });
  };

  const load = async (): Promise<void> => {
    if (!content) return;
    try {
      const [generalResponse, rolePermissionsResponse, serviceTypesResponse, liveInputsResponse, schedulesResponse, retentionResponse, pcoSyncSettingsResponse] =
        await Promise.all([
          fetch("/api/admin/general-settings", { headers: { Accept: "application/json" } }),
          fetch("/api/admin/role-permissions", { headers: { Accept: "application/json" } }),
          fetch("/api/admin/service-types", { headers: { Accept: "application/json" } }),
          fetch("/api/admin/live-inputs", { headers: { Accept: "application/json" } }),
          fetch("/api/admin/schedules", { headers: { Accept: "application/json" } }),
          fetch("/api/admin/retention-settings", { headers: { Accept: "application/json" } }),
          fetch("/api/admin/pco-services/settings", { headers: { Accept: "application/json" } }),
        ]);
      for (const response of [
        generalResponse,
        rolePermissionsResponse,
        serviceTypesResponse,
        liveInputsResponse,
        schedulesResponse,
        retentionResponse,
        pcoSyncSettingsResponse,
      ]) {
        if (!response.ok) throw new Error(await errorMessage(response));
      }
      const general = (await generalResponse.json()) as GeneralSettingsResponse;
      const rolePermissions = (await rolePermissionsResponse.json()) as RolePermissionsResponse;
      const serviceTypes = (
        (await serviceTypesResponse.json()) as ServiceTypesConfigurationResponse
      ).serviceTypes;
      const liveInputs = (
        (await liveInputsResponse.json()) as LiveInputsConfigurationResponse
      ).liveInputs;
      const schedules = (
        (await schedulesResponse.json()) as SchedulesConfigurationResponse
      ).schedules;
      const retention = (await retentionResponse.json()) as RetentionSettingsResponse;
      const pcoSyncSettings = (await pcoSyncSettingsResponse.json()) as PcoServicesSyncSettingsResponse;

      content.innerHTML = `
        ${configurationSection({
          id: "general",
          eyebrow: "Brand and appearance",
          title: "General",
          status: `${general.organizationName} · ${general.siteName} · ${general.themeTemplate === "friends-church-online" ? "Friends Church" : general.themeTemplate === "default" ? "Default" : "Custom"}`,
          body: generalSettingsForm(general),
        })}
        ${configurationSection({
          id: "video-playback",
          eyebrow: "Player features",
          title: "Video Playback",
          status: `${general.videoCommentsEnabled ? "Notes and comments enabled" : "Notes and comments disabled"} · ${general.viewingAnalyticsEnabled ? "Analytics enabled" : "Analytics disabled"}`,
          body: videoPlaybackSettingsForm(general),
        })}
        ${configurationSection({
          id: "user-roles",
          eyebrow: "Authorization",
          title: "User Roles",
          status: "Administrator-only configuration",
          body: rolePermissionMatrix(rolePermissions),
        })}
        ${configurationSection({
          id: "service-types",
          eyebrow: "Content categories",
          title: "Service Types",
          status: `${serviceTypes.length} configured`,
          body: `<div class="configuration-list">
            ${serviceTypeForm()}
            ${serviceTypes.map((item) => serviceTypeForm(item)).join("")}
          </div>`,
        })}
        ${configurationSection({
          id: "live-inputs",
          eyebrow: "Capture sources",
          title: "Live Inputs",
          status: `${liveInputs.length} configured`,
          body: `<div class="configuration-list">
            ${liveInputForm()}
            ${liveInputs.length ? liveInputs.map((item) => liveInputForm(item)).join("") : '<p class="empty-state">No Live Inputs are configured yet.</p>'}
          </div>`,
        })}
        ${configurationSection({
          id: "schedules",
          eyebrow: "Recurring capture",
          title: "Schedules",
          status: `${schedules.filter((item) => item.isActive).length} active · ${schedules.length} total`,
          body: `<div class="generation-actions configuration-section-actions">
              <span>${schedules.filter((item) => item.isActive).length} active · ${schedules.length} total</span>
              <button class="primary-button compact" id="generate-events" type="button">Generate upcoming Events</button>
            </div><div class="configuration-list">
            ${scheduleForm(serviceTypes, liveInputs)}
            ${schedules.map((item) => scheduleForm(serviceTypes, liveInputs, item)).join("")}
          </div>`,
        })}
        ${configurationSection({
          id: "retention",
          eyebrow: "Storage lifecycle",
          title: "Retention",
          status: `${retention.onlineDays} days online · ${retention.restoredOnlineDays} days restored`,
          body: retentionSettingsForm(retention),
        })}
        ${configurationSection({
          id: "integrations",
          eyebrow: "Connected services",
          title: "Integrations",
          status: "Planning Center Services",
          body: `<div class="integration-panel">
            <div class="integration-heading"><div><h3>Planning Center Services</h3><p>Refresh Plans, items, people, and Event associations using the configured server-side integration.</p></div>
            <button class="secondary-button compact provider-sync-button" id="sync-pco-services" type="button">Sync PCO Service Manually</button></div>
            <p class="form-message" id="integration-notice" role="status" aria-live="polite"></p>
            ${pcoSyncSettingsForm(pcoSyncSettings)}
          </div>`,
        })}
      `;
      wireForms();
      wireGeneralSettings();
      wireVideoPlaybackSettings(general);
      wireRolePermissions();
      wireRetentionSettings();
      wireIntegrations();
      document
        .querySelector<HTMLButtonElement>("#generate-events")
        ?.addEventListener("click", (event) => {
          void generateEvents(event.currentTarget as HTMLButtonElement);
        });
      if (window.location.hash) {
        requestAnimationFrame(() => openConfigurationSection(window.location.hash, "auto"));
      }
    } catch (error) {
      content.innerHTML = `<p class="error-message">${escapeHtml(
        error instanceof Error ? error.message : "Configuration could not be loaded.",
      )}</p>`;
    }
  };

  const generateEvents = async (button: HTMLButtonElement): Promise<void> => {
    try {
      button.disabled = true;
      button.textContent = "Generating…";
      const response = await fetch("/api/admin/events/generate", {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: "{}",
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      const result = (await response.json()) as EventGenerationResult;
      if (notice) {
        notice.textContent = `${result.eventsCreated} Event${result.eventsCreated === 1 ? "" : "s"} and ${result.videosCreated} placeholder Video${result.videosCreated === 1 ? "" : "s"} created through ${result.endDateExclusiveLocal}. ${result.existingEvents} existing occurrence${result.existingEvents === 1 ? " was" : "s were"} preserved.`;
        notice.className = "admin-notice visible";
      }
    } catch (error) {
      if (notice) {
        notice.textContent =
          error instanceof Error ? error.message : "Events could not be generated.";
        notice.className = "admin-notice visible error-message";
      }
    } finally {
      button.disabled = false;
      button.textContent = "Generate upcoming Events";
    }
  };

  const wireForms = (): void => {
    document
      .querySelectorAll<HTMLFormElement>(".configuration-form")
      .forEach((form) => {
        form.querySelector<HTMLButtonElement>(".clear-schedule-end-date")
          ?.addEventListener("click", () => {
            const endDate = form.elements.namedItem("effectiveEndDate") as HTMLInputElement | null;
            if (endDate) {
              endDate.value = "";
            }
          });
        form.querySelector<HTMLButtonElement>(".upload-schedule-thumbnail")
          ?.addEventListener("click", (event) => {
            void uploadScheduleThumbnail(form, event.currentTarget as HTMLButtonElement);
          });
        form.querySelector<HTMLButtonElement>(".remove-schedule-thumbnail")
          ?.addEventListener("click", (event) => {
            void removeScheduleThumbnail(form, event.currentTarget as HTMLButtonElement);
          });
        form.addEventListener("submit", (event) => {
          event.preventDefault();
          void save(form);
        });
      });
    document
      .querySelectorAll<HTMLButtonElement>(".configuration-delete-button")
      .forEach((button) => {
        button.addEventListener("click", () => {
          const form = button.closest<HTMLFormElement>(".configuration-form");
          if (form) void deleteConfiguration(form, button);
        });
      });
  };

  const uploadScheduleThumbnail = async (
    form: HTMLFormElement,
    button: HTMLButtonElement,
  ): Promise<void> => {
    const id = form.dataset.resourceId;
    const input = form.elements.namedItem("scheduleThumbnail") as HTMLInputElement | null;
    const file = input?.files?.[0];
    const message = form.querySelector<HTMLElement>(".form-message");
    if (!id || !file) {
      if (message) message.textContent = "Choose a thumbnail image.";
      return;
    }
    if (!(["image/jpeg", "image/png", "image/webp"] as string[]).includes(file.type)) {
      if (message) message.textContent = "Choose a JPEG, PNG, or WebP image.";
      return;
    }
    if (file.size > 1_500_000) {
      if (message) message.textContent = "The thumbnail must be 1.5 MB or smaller.";
      return;
    }
    try {
      button.disabled = true;
      button.textContent = "Uploading…";
      if (message) { message.textContent = "Uploading Schedule thumbnail…"; message.className = "form-message"; }
      const response = await fetch(`/api/admin/schedules/${encodeURIComponent(id)}/thumbnail`, {
        method: "PUT",
        headers: { Accept: "application/json", "Content-Type": file.type },
        body: file,
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      if (notice) {
        notice.textContent = "Schedule thumbnail uploaded and audited.";
        notice.className = "admin-notice visible";
      }
      await load();
    } catch (error) {
      button.disabled = false;
      button.textContent = "Upload Custom Thumbnail";
      if (message) {
        message.textContent = error instanceof Error ? error.message : "The Schedule thumbnail could not be uploaded.";
        message.className = "form-message error-message";
      }
    }
  };

  const removeScheduleThumbnail = async (
    form: HTMLFormElement,
    button: HTMLButtonElement,
  ): Promise<void> => {
    const id = form.dataset.resourceId;
    if (!id || !window.confirm("Remove this Schedule thumbnail? Events without an individual override will return to Planning Center artwork or the default abbreviation.")) return;
    const message = form.querySelector<HTMLElement>(".form-message");
    try {
      button.disabled = true;
      button.textContent = "Removing…";
      const response = await fetch(`/api/admin/schedules/${encodeURIComponent(id)}/thumbnail`, {
        method: "DELETE",
        headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
        body: JSON.stringify({ confirmation: "DELETE" }),
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      if (notice) {
        notice.textContent = "Schedule thumbnail removed and audited.";
        notice.className = "admin-notice visible";
      }
      await load();
    } catch (error) {
      button.disabled = false;
      button.textContent = "Remove Custom Thumbnail";
      if (message) {
        message.textContent = error instanceof Error ? error.message : "The Schedule thumbnail could not be removed.";
        message.className = "form-message error-message";
      }
    }
  };

  const wireGeneralSettings = (): void => {
    const form = document.querySelector<HTMLFormElement>("#general-settings-form");
    if (!form) return;
    form.querySelectorAll<HTMLInputElement>("[data-color-picker]").forEach((picker) => {
      const name = picker.dataset.colorPicker;
      const text = name ? form.elements.namedItem(name) as HTMLInputElement : null;
      picker.addEventListener("input", () => { if (text) text.value = picker.value.toUpperCase(); });
      text?.addEventListener("input", () => {
        if (/^#[0-9A-Fa-f]{6}$/u.test(text.value)) picker.value = text.value;
      });
    });
    const manualThemeSettings = form.querySelector<HTMLFieldSetElement>("[data-manual-theme-settings]");
    const templateManagedNote = form.querySelector<HTMLElement>("[data-template-managed-note]");
    const setThemeTemplateState = (): void => {
      const selected = form.querySelector<HTMLInputElement>('input[name="themeTemplate"]:checked')?.value ?? "custom";
      const isCustom = selected === "custom";
      if (manualThemeSettings) manualThemeSettings.disabled = !isCustom;
      if (templateManagedNote) {
        templateManagedNote.textContent = isCustom
          ? "These values apply when the Custom template is selected."
          : `Managed by the ${selected === "default" ? "Default" : "Friends Church"} template. Select Custom to edit these values.`;
      }
    };
    form.querySelectorAll<HTMLInputElement>('input[name="themeTemplate"]').forEach((input) => {
      input.addEventListener("change", setThemeTemplateState);
    });
    setThemeTemplateState();
    const showLogo = form.elements.namedItem("showLogo") as HTMLInputElement;
    const logoFile = form.elements.namedItem("logoFile") as HTMLInputElement;
    const logoPreview = form.querySelector<HTMLElement>("[data-logo-preview]");
    const setLogoState = (): void => {
      logoFile.disabled = !showLogo.checked;
      if (logoPreview) logoPreview.classList.toggle("logo-disabled", !showLogo.checked);
    };
    showLogo.addEventListener("change", setLogoState);
    setLogoState();
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void saveGeneralSettings(form);
    });
  };

  const fileAsDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Logo could not be read.")));
    reader.addEventListener("error", () => reject(new Error("Logo could not be read.")));
    reader.readAsDataURL(file);
  });

  const saveGeneralSettings = async (form: HTMLFormElement): Promise<void> => {
    const message = form.querySelector<HTMLElement>(".form-message");
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    try {
      const showLogo = (form.elements.namedItem("showLogo") as HTMLInputElement).checked;
      const file = showLogo ? (form.elements.namedItem("logoFile") as HTMLInputElement).files?.[0] : undefined;
      if (file && file.size > 256_000) throw new Error("The logo must be no larger than 256 KB.");
      const logoDataUrl = showLogo
        ? file ? await fileAsDataUrl(file) : textValue(form, "logoDataUrl") || null
        : null;
      const body: PublicSiteSettings = {
        organizationName: textValue(form, "organizationName"),
        siteName: textValue(form, "siteName"),
        themeTemplate: textValue(form, "themeTemplate") as PublicSiteSettings["themeTemplate"],
        backgroundColor: textValue(form, "backgroundColor"),
        fontColor: textValue(form, "fontColor"),
        featureColor: textValue(form, "featureColor"),
        fontFamily: textValue(form, "fontFamily") as PublicSiteSettings["fontFamily"],
        logoDataUrl,
        videoCommentsEnabled: (form.elements.namedItem("videoCommentsEnabled") as HTMLInputElement).value === "true",
        viewingAnalyticsEnabled: (form.elements.namedItem("viewingAnalyticsEnabled") as HTMLInputElement).value === "true",
      };
      if (button) button.disabled = true;
      if (message) message.textContent = "Saving…";
      const response = await fetch("/api/admin/general-settings", {
        method: "PATCH",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      if (notice) { notice.textContent = "General settings saved and audited. Reloading the updated brand…"; notice.className = "admin-notice visible"; }
      window.location.reload();
    } catch (error) {
      if (message) { message.textContent = error instanceof Error ? error.message : "General settings could not be saved."; message.className = "form-message error-message"; }
      if (button) button.disabled = false;
    }
  };

  const wireVideoPlaybackSettings = (settings: GeneralSettingsResponse): void => {
    const form = document.querySelector<HTMLFormElement>("#video-playback-settings-form");
    form?.addEventListener("submit", (event) => {
      event.preventDefault();
      void saveVideoPlaybackSettings(form, settings);
    });
  };

  const saveVideoPlaybackSettings = async (form: HTMLFormElement, settings: GeneralSettingsResponse): Promise<void> => {
    const message = form.querySelector<HTMLElement>(".form-message");
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    try {
      if (button) button.disabled = true;
      if (message) message.textContent = "Saving…";
      const response = await fetch("/api/admin/general-settings", {
        method: "PATCH",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          ...settings,
          videoCommentsEnabled: (form.elements.namedItem("videoCommentsEnabled") as HTMLInputElement).checked,
          viewingAnalyticsEnabled: (form.elements.namedItem("viewingAnalyticsEnabled") as HTMLInputElement).checked,
        }),
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      if (notice) { notice.textContent = "Video playback settings saved and audited. Reloading…"; notice.className = "admin-notice visible"; }
      window.location.reload();
    } catch (error) {
      if (message) { message.textContent = error instanceof Error ? error.message : "Video playback settings could not be saved."; message.className = "form-message error-message"; }
      if (button) button.disabled = false;
    }
  };

  const wireRolePermissions = (): void => {
    document.querySelector<HTMLButtonElement>("#save-role-permissions")?.addEventListener("click", (event) => {
      void saveRolePermissions(event.currentTarget as HTMLButtonElement);
    });
  };

  const wireRetentionSettings = (): void => {
    const form = document.querySelector<HTMLFormElement>("#retention-settings-form");
    form?.addEventListener("submit", (event) => {
      event.preventDefault();
      void saveRetentionSettings(form);
    });
  };

  const saveRetentionSettings = async (form: HTMLFormElement): Promise<void> => {
    const message = form.querySelector<HTMLElement>(".form-message");
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    try {
      if (button) button.disabled = true;
      if (message) message.textContent = "Saving…";
      const response = await fetch("/api/admin/retention-settings", {
        method: "PATCH",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          onlineDays: Number(textValue(form, "onlineDays")),
          restoredOnlineDays: Number(textValue(form, "restoredOnlineDays")),
        }),
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      if (notice) {
        notice.textContent = "Retention settings saved and audited.";
        notice.className = "admin-notice visible";
      }
      await load();
    } catch (error) {
      if (message) {
        message.textContent = error instanceof Error ? error.message : "Retention settings could not be saved.";
        message.className = "form-message error-message";
      }
      if (button) button.disabled = false;
    }
  };

  const saveRolePermissions = async (button: HTMLButtonElement): Promise<void> => {
    const message = document.querySelector<HTMLElement>("#permission-message");
    try {
      button.disabled = true;
      if (message) message.textContent = "Saving all role permissions…";
      for (const role of configurableRoles) {
        const permissionKeys = Array.from(document.querySelectorAll<HTMLInputElement>(`input[data-role="${role}"]:checked:not(:disabled)`))
          .map((input) => input.dataset.permissionKey).filter((key): key is string => Boolean(key));
        const response = await fetch("/api/admin/role-permissions", {
          method: "PATCH",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ role, permissionKeys }),
        });
        if (!response.ok) throw new Error(await errorMessage(response));
      }
      if (notice) { notice.textContent = "User Role permissions saved and audited."; notice.className = "admin-notice visible"; }
      await load();
    } catch (error) {
      if (message) { message.textContent = error instanceof Error ? error.message : "User Roles could not be saved."; message.className = "form-message error-message"; }
      button.disabled = false;
    }
  };

  const save = async (form: HTMLFormElement): Promise<void> => {
    const message = form.querySelector<HTMLElement>(".form-message");
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    try {
      const resource = form.dataset.resource;
      const id = form.dataset.resourceId;
      const endpoint =
        resource === "service-type"
          ? "/api/admin/service-types"
          : resource === "live-input"
            ? "/api/admin/live-inputs"
            : "/api/admin/schedules";
      const body =
        resource === "service-type"
          ? serviceTypeInput(form)
          : resource === "live-input"
            ? liveInputInput(form)
            : scheduleInput(form);
      if (button) button.disabled = true;
      if (message) {
        message.textContent = "Saving…";
        message.className = "form-message";
      }
      const response = await fetch(
        id ? `${endpoint}/${encodeURIComponent(id)}` : endpoint,
        {
          method: id ? "PATCH" : "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!response.ok) throw new Error(await errorMessage(response));
      if (notice) {
        notice.textContent = "Configuration saved and audited.";
        notice.className = "admin-notice visible";
      }
      await load();
    } catch (error) {
      if (message) {
        message.textContent =
          error instanceof Error ? error.message : "Configuration could not be saved.";
        message.className = "form-message error-message";
      }
      if (button) button.disabled = false;
    }
  };

  const deleteConfiguration = async (
    form: HTMLFormElement,
    button: HTMLButtonElement,
  ): Promise<void> => {
    const resource = form.dataset.resource;
    const id = form.dataset.resourceId;
    const title = form.closest(".config-card")?.querySelector("summary strong")?.textContent?.trim() ?? "this item";
    if (!id || !["service-type", "live-input", "schedule"].includes(resource ?? "")) return;
    const resourceLabel = resource === "service-type"
      ? "Service Type"
      : resource === "live-input"
        ? "Live Input"
        : "Schedule";
    const consequence = resource === "service-type"
      ? "Deletion will be blocked if a user scope, Schedule, Planning Center Plan, or Event still uses it."
      : resource === "live-input"
        ? "Deletion will be blocked if a Schedule, Event, or Source Recording still uses it."
        : "Existing Events will be preserved, but this recurring window will stop generating new Events.";
    if (!window.confirm(`Delete ${resourceLabel} “${title}”?\n\n${consequence}\n\nThis action cannot be undone.`)) return;
    const message = form.querySelector<HTMLElement>(".form-message");
    try {
      button.disabled = true;
      button.textContent = "Deleting…";
      if (message) { message.textContent = "Deleting…"; message.className = "form-message"; }
      const endpoint = resource === "service-type"
        ? "/api/admin/service-types"
        : resource === "live-input"
          ? "/api/admin/live-inputs"
          : "/api/admin/schedules";
      const response = await fetch(`${endpoint}/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
        body: JSON.stringify({ confirmation: "DELETE" }),
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      if (notice) {
        notice.textContent = `${resourceLabel} deleted and audited.`;
        notice.className = "admin-notice visible";
      }
      await load();
    } catch (error) {
      button.disabled = false;
      button.textContent = `Delete ${resourceLabel}`;
      if (message) {
        message.textContent = error instanceof Error ? error.message : `The ${resourceLabel} could not be deleted.`;
        message.className = "form-message error-message";
      }
    }
  };

  await load();
};
